import { useCallback, useMemo } from 'react';

import { electronApis } from '@/features/electron-api';
import { logoutUser } from '@/utils/auth';

/** Whether the error comes from the local database, directly or wrapped in Drizzle's error with the full SQL statement. */
const isLocalDataError = (error: Error) =>
  error.message.startsWith('local-db:') ||
  error.message.startsWith('Failed query:');

export function useErrorReception(error: Error) {
  const isLocalData = useMemo(() => isLocalDataError(error), [error]);

  const title = useMemo(() => {
    if (error.name === 'TokenExpiredError') {
      return 'Session expired.';
    }

    if (isLocalData) {
      return "Littlebird couldn't load your data.";
    }

    return 'Something went wrong.';
  }, [error, isLocalData]);

  const message = useMemo(() => {
    if (error.name === 'TokenExpiredError') {
      return 'Your session has expired. Please login again.';
    }

    if (isLocalData) {
      return 'Reload this tab. If it happens again, close your other Littlebird tabs, then reload.';
    }

    if (error instanceof Error) {
      return error.message;
    }

    return JSON.stringify(error) + new Error().stack;
  }, [error, isLocalData]);

  const actionLabel = useMemo(() => {
    if (error.name === 'TokenExpiredError') {
      return 'Login';
    }

    return 'Reload';
  }, [error]);

  const action = useCallback(() => {
    if (error.name === 'TokenExpiredError') {
      logoutUser('session_expired');

      return;
    }

    electronApis.auth.requestToken('frontend', { force: true }).catch(() => {
      // ignore
    });

    window.location.reload();
  }, [error]);

  return {
    title,
    message,
    action,
    actionLabel,
  };
}
